/**
 * Geocoding Utility
 *
 * Simple utility for converting addresses to coordinates.
 *
 * DDD Note: This is INFRASTRUCTURE only - not a domain service.
 * Following DDD architect recommendation: Keep it simple for MVP.
 * - No caching (compute on-demand)
 * - No retry logic (graceful degradation)
 * - No backend service (client-side only)
 * - Returns null on failure (jobs just won't appear on map)
 */

export interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Geocode an address using Mapbox Geocoding API
 *
 * MVP Implementation:
 * - Direct API call from browser
 * - No caching, no retries
 * - Graceful failure (returns null)
 * - Simple and testable
 *
 * @param address - Full address string to geocode
 * @returns Coordinates or null if geocoding fails
 */
export async function geocodeAddress(
  address: string
): Promise<Coordinates | null> {
  if (!address || address.trim() === '') {
    return null;
  }

  try {
    // Use Mapbox Geocoding API
    // Note: In production, this should use an env var for the token
    // For now, we'll use a placeholder that should be replaced
    const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

    if (!accessToken) {
      console.warn('Mapbox access token not configured');
      return null;
    }

    const encodedAddress = encodeURIComponent(address);
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${accessToken}&limit=1`;

    const response = await fetch(url);

    if (!response.ok) {
      console.warn(`Geocoding failed for address: ${address}`, response.status);
      return null;
    }

    const data = await response.json();

    if (!data.features || data.features.length === 0) {
      console.warn(`No geocoding results for address: ${address}`);
      return null;
    }

    const [longitude, latitude] = data.features[0].center;

    return {
      latitude,
      longitude,
    };
  } catch (error) {
    console.warn(`Geocoding error for address: ${address}`, error);
    return null;
  }
}

/**
 * Batch geocode multiple addresses
 *
 * Note: This is just a convenience wrapper that calls geocodeAddress
 * multiple times. NOT a sophisticated batching system.
 *
 * @param addresses - Array of addresses to geocode
 * @returns Array of coordinates (null for failures)
 */
export async function geocodeAddresses(
  addresses: string[]
): Promise<(Coordinates | null)[]> {
  return Promise.all(addresses.map((addr) => geocodeAddress(addr)));
}
